import type { Styles } from '@adornis/ass/style.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { NO_CHANGE } from '@adornis/router/client/routing-state-signal.js';
import type { LivekitControllerRemoteElement } from '@adornis/web-rtc/client/livekitController.js';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import '../ils-wifi-loader-animation.js';
import { ILSVideoChat, type ILSVideoChatBox } from './ils-video-chat.js';

@customElement('ils-evaluation-video-chat')
export class ILSEvaluationVideoChat extends ILSVideoChat {
  override render() {
    return html`
      <x-flex space="sm"> ${this.__toolbar()} ${this.__allVideoFeeds()} ${this.__contentWrapper()} </x-flex>
    `;
  }

  private __toolbar() {
    return html`
      <x-flex horizontal space="sm" crossaxis-center ${css({ width: '100%' })}>
        ${this.__videoIcon('mic', 'mic_off', 'microphone')} ${this.__videoIcon('videocam', 'videocam_off', 'camera')}

        <x-button
          tone="error"
          ${css({ marginLeft: 'auto' })}
          @click=${() => {
            goTo({ path: '/start/evaluation-overview', searchParams: NO_CHANGE });
          }}
        >
          Verlassen
        </x-button>
      </x-flex>
    `;
  }

  private __contentWrapper() {
    return html` <x-flex> ${this.__content()} </x-flex> `;
  }

  private __content() {
    const remoteMedia = [...this.livekitController.remoteMedia()];
    const memberCount = remoteMedia.filter(
      ([participant, boxElement]) => !(boxElement as ILSVideoChatBox).isInvisible(),
    ).length;
    const localErrors = this.livekitController.getLocalErrors();

    if (!localErrors.size && memberCount > 0) return this.__slotContent();
    if (localErrors.size) return this.retryStream();
    return this.emptyRoom();
  }

  private __slotContent() {
    return html` <slot></slot> `;
  }

  private __allVideoFeeds() {
    const remoteMedia = [...this.livekitController.remoteMedia()];
    const memberCount = remoteMedia.filter(
      ([participant, boxElement]) => !(boxElement as ILSVideoChatBox).isInvisible(),
    ).length;
    const localErrors = this.livekitController.getLocalErrors();
    return html`
      <x-flex
        horizontal
        crossaxis-center
        center
        space="sm"
        wrap
        ${css({ maxWidth: '80%', padding: this.spacing.sm, margin: 'auto' })}
      >
        <!-- own video -->
        ${this.__ownVideoFeed()}

        <!-- other video feeds -->
        ${localErrors.size || memberCount === 0
          ? nothing
          : repeat(remoteMedia, ([member, boxElement]) => this.remoteVideo(boxElement))}
      </x-flex>
    `;
  }

  private __videoIcon(iconEnabled: string, iconDisabled: string, kind: 'camera' | 'microphone') {
    const localState = this.livekitController.getLocalTrackState();
    const toggle = (kind === 'camera' ? this.toggleCamera : this.toggleMicrophone).bind(this);

    return html`
      <x-flex
        crossaxis-center
        center
        ${css({
          background: localState[kind] ? 'green' : 'red',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          cursor: 'pointer',
        })}
        @click=${() => {
          toggle();
        }}
      >
        <x-icon
          ${css({
            color: '#fff',
            fontSize: '24px',
          })}
        >
          ${localState[kind] ? iconEnabled : iconDisabled}
        </x-icon>
      </x-flex>
    `;
  }

  private __ownVideoFeed() {
    return html`
      <div ${css({ position: 'relative' })} video-wrapper>
        <video ${ref(this.ownVideoElement)} autoplay muted></video>
      </div>
    `;
  }

  override remoteVideo(element: LivekitControllerRemoteElement): Renderable {
    return html` <div ${css({ position: 'relative' })} video-wrapper other-video>${element}</div> `;
  }

  override emptyRoom() {
    return html`
      <x-flex flex center crossaxis-center space="lg" h100>
        <ils-wifi-loader-animation></ils-wifi-loader-animation>
        <x-h2> Es wird nach weiteren Teilnehmern gesucht... </x-h2>
      </x-flex>
    `;
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        '*[video-wrapper]': {
          width: 'min(80%,200px)',
        },
        '*[other-video]': {
          width: '95%',
        },
        '*[video-wrapper]>video': {
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: this.sizes.borderRadius,
          overflow: 'hidden',
          objectFit: 'cover',
        },
      },
    ];
  }
}
